<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="800px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon left color="white">mdi-format-list-bulleted-square</v-icon>
        {{ $t('variant.title.create_all') }}
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-6" style="max-height: 600px">
        <v-alert
          v-if="variants.length >= 3"
          type="warning"
          text
          dense
          class="mb-4"
        >
          {{ $t('variant.message.exceeded') }}
        </v-alert>

        <v-expansion-panels
          v-model="expandedPanels"
          multiple
          accordion
        >
          <v-expansion-panel
            v-for="(variant, index) in variants"
            :key="index"
          >
            <v-expansion-panel-header>
              <template v-slot:default="{ open }">
                <v-row no-gutters align="center">
                  <v-col cols="6">
                    <v-fade-transition leave-absolute>
                      <span v-if="open" class="text-subtitle-1 font-weight-medium">
                        {{ variant.name || `Variant ${index + 1}` }}
                      </span>
                      <v-row v-else no-gutters style="width: 100%">
                        <v-col cols="12">
                          <strong>{{ variant.name || `Variant ${index + 1}` }}</strong>
                        </v-col>
                        <v-col cols="12">
                          <v-chip
                            v-for="(option, idx) in variant.options.slice(0, 3)"
                            :key="idx"
                            x-small
                            class="mr-1"
                          >
                            {{ option }}
                          </v-chip>
                          <span v-if="variant.options.length > 3" class="text-caption grey--text">
                            +{{ variant.options.length - 3 }} more
                          </span>
                        </v-col>
                      </v-row>
                    </v-fade-transition>
                  </v-col>
                  <v-col cols="auto" class="ml-auto">
                    <v-chip
                      small
                      :color="variant.options.length > 0 ? 'success' : 'grey'"
                      text-color="white"
                    >
                      {{ variant.options.length }} options
                    </v-chip>
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <v-card flat>
                <v-card-text class="pa-0 pt-4">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="variants[index].name"
                        :label="$t('variant.label.variant_name')"
                        :placeholder="`e.g., Size, Color, Material`"
                        outlined
                        dense
                        :autofocus="index === 0"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary">mdi-label</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <v-combobox
                        v-model="variants[index].options"
                        :label="$t('variant.label.options')"
                        :placeholder="$t('variant.message.options_guide')"
                        chips
                        clearable
                        multiple
                        outlined
                        dense
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary">mdi-tag-multiple</v-icon>
                        </template>
                        <template v-slot:selection="{ attrs, item, select, selected }">
                          <v-chip
                            v-bind="attrs"
                            :input-value="selected"
                            close
                            @click="select"
                            @click:close="removeOption(index, variants[index].options.indexOf(item))"
                            small
                            color="primary"
                            outlined
                          >
                            <strong>{{ item }}</strong>
                          </v-chip>
                        </template>
                      </v-combobox>
                    </v-col>

                    <v-col cols="12" class="text-right">
                      <v-btn
                        color="error"
                        text
                        small
                        @click="removeVariant(index)"
                      >
                        <v-icon left small>mdi-delete</v-icon>
                        Remove Variant
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-row class="mt-4">
          <v-col>
            <v-btn
              color="success"
              outlined
              block
              @click="addVariant"
              :disabled="variants.length >= 3"
            >
              <v-icon left>mdi-plus</v-icon>
              {{ $t('variant.title.create') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          text
          large
          @click.prevent="safeCloseVariant"
        >
          {{ $t('button.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          large
          :disabled="!variants.length"
          :loading="loading"
          @click.prevent="saveVariant"
        >
          <v-icon left>mdi-check</v-icon>
          {{ $t('button.apply') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'VariantDialogVuetify',
  props: {
    display: {
      type: Boolean,
      required: true
    },
    item: {
      type: Object,
      required: false,
      default: () => ({})
    },
    prevVariants: {
      type: Array,
      required: false,
      default: () => ([])
    }
  },
  data () {
    return {
      variants: [],
      snapshot: [],
      loading: false,
      expandedPanels: []
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.display
      },
      set (value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    edit () {
      return this.item && this.item.id
    },
    defaultVariant: () => ([{
      name: '',
      inputValue: '',
      options: []
    }]),
    safeToExit () {
      return JSON.stringify(this.variants) === JSON.stringify(this.snapshot)
    }
  },
  watch: {
    display (val) {
      if (val) {
        this.open()
      }
    }
  },
  methods: {
    async open () {
      if (this.prevVariants && this.prevVariants.length > 0) {
        this.variants = JSON.parse(JSON.stringify(this.prevVariants))
      } else {
        if (this.edit) {
          const variants = await this.$variantService.getItemVariant(this.item.id)
          if (variants && variants.length > 0) {
            this.variants = variants.map(v => ({
              id: v.id,
              name: v.name,
              options: v.options.map(vo => vo.name),
              inputValue: ''
            }))
          } else {
            this.variants = JSON.parse(JSON.stringify(this.defaultVariant))
          }
        } else {
          this.variants = JSON.parse(JSON.stringify(this.defaultVariant))
        }
      }
      this.snapshot = JSON.parse(JSON.stringify(this.variants))
      // Expand all panels by default
      this.expandedPanels = this.variants.map((_, index) => index)
    },
    safeCloseVariant () {
      if (this.safeToExit) {
        this.$emit('close')
      } else {
        this.askQuestion('You have unsaved changes. Do you want to continue?',
          () => (this.$emit('close')), 'Save changes')
      }
    },
    closed () {
      this.variants = []
      this.expandedPanels = []
    },
    removeOption (vIndex, oIndex) {
      this.variants[vIndex].options.splice(oIndex, 1)
    },
    addVariant () {
      if (this.variants.length < 3) {
        this.variants.push({
          name: '',
          item_id: this.item.id,
          options: [],
          inputValue: ''
        })
        // Expand the newly added panel
        this.$nextTick(() => {
          this.expandedPanels.push(this.variants.length - 1)
        })
      } else {
        this.notify(this.$t('variant.message.exceeded'), this.$t('variant.label.exceeded'), 'error')
      }
    },
    removeVariant (index) {
      this.variants.splice(index, 1)
      // Update expanded panels
      this.expandedPanels = this.expandedPanels.filter(i => i < this.variants.length)
    },
    async saveVariant () {
      if (this.variants.length) {
        const data = this.variants.filter(v => {
          return v.name && v.options.length > 0
        })

        // Check for empty fields
        const hasEmptyFields = this.variants.some(v => !v.name || v.options.length === 0)
        if (hasEmptyFields) {
          this.showWarning(this.$t('variant.message.empty_fields'), 'Verify')
          return
        }

        this.$emit('created', data.length ? data : [])
        this.variants = JSON.parse(JSON.stringify(this.snapshot))
        this.$emit('close')
      }
    }
  }
}
</script>

<style scoped>
::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 16px;
}
</style>
